import { SENDA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import type { CorniceRules, GearRules, GolemRules, WorkshopRules } from '../types.ts';
import { axleHex, type CorniceLayout, type GearLayout, type WorkshopLayout } from './taller-layout.ts';

/**
 * Nadd's workshop's rules from their layouts (docs/sendas/artifice.md, chapter 1): the fight's builder
 * (campaign/sendas.ts) and the renderer's scenes build them the same way. SENDA.cornice, golem, workshop and gears.
 */

export const corniceRules = (L: CorniceLayout): CorniceRules => {
  const C = SENDA.cornice;
  return {
    way: L.way.map((p) => ({ x: p.x, y: p.y })),
    rests: [...L.restAt],
    nests: L.nestAt.map((n) => ({ x: n.x, y: n.y, row: n.row })),
    door: { x: L.door.x, y: L.door.y },
    pace: C.pace,
    wait: C.wait,
    reach: C.reach,
    stone: { ...C.stone, gap: [C.stone.gap[0], C.stone.gap[1]] },
    sling: { ...C.sling },
    crows: { ...C.crows },
    crawl: C.crawl,
    rise: C.rise,
    downs: C.downs,
  };
};

/** The golems' rules: their life and the Overseer's in hit points (seconds of D, worked out by the caller); `lamp`: off in the lift's hall. */
export const golemRules = (hp: number, bossHp: number, lamp = true): GolemRules => {
  const G = SENDA.golem;
  return {
    hp,
    pace: G.pace,
    turn: G.turn,
    reach: G.reach,
    arc: G.arc,
    warn: G.warn,
    heroBp: G.heroBp,
    blows: G.blows,
    rest: G.rest,
    off: G.off,
    back: G.back,
    frontBp: G.frontBp,
    lamp: { ...G.lamp, range: lamp ? G.lamp.range : 0 },
    spin: { ...G.spin },
    stamp: { ...G.stamp },
    boss: { hp: bossHp, heroBp: G.boss.heroBp, blows: G.boss.blows, capBp: G.boss.capBp, open: G.boss.open, pace: G.boss.pace, turn: G.boss.turn, reach: G.boss.reach, warn: G.boss.warn, arc: G.boss.arc },
  };
};

/**
 * `radius`: the board's (the clearing's, its outskirts round it: the layout's radius + 2). With the layout's own the
 * cell ids fell off the board and a tap sent the hero nowhere (docs/RENDIMIENTO.md).
 */
export const workshopRules = (L: WorkshopLayout, radius: number, dunnaHit: number): WorkshopRules => {
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  return {
    halls: L.halls.map((h) => ({ at: { ...h.at }, stair: h.stair.map(id).sort((a, b) => a - b), posts: h.posts.map((p) => ({ ...p })) })),
    lift: { ...L.lift },
    reach: SENDA.workshop.reach,
    chain: SENDA.workshop.chain,
    dunna: { every: SENDA.workshop.dunna.every, hit: dunnaHit, range: SENDA.workshop.dunna.range },
  };
};

export const gearRules = (L: GearLayout, radius: number, dunnaHit: number): GearRules => {
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  const G = SENDA.gears;
  return {
    axles: L.axles.map((a) => id(axleHex(a))),
    adj: L.adj.map((x) => [...x]),
    wheel: L.wheel,
    want: L.want,
    pieces: [...L.pieces],
    lie: L.lie.map(id),
    posts: L.posts.map((p) => ({ ...p })),
    boss: { ...L.boss },
    lift: { ...L.lift },
    dunnaAt: hexToWorld(-5, 4),
    stand: G.stand,
    reach: G.reach,
    carryBp: [...G.carryBp],
    every: G.every,
    first: G.first,
    cap: G.cap,
    settle: G.settle,
    dunna: { every: G.dunna.every, hit: dunnaHit, range: G.dunna.range },
  };
};
